import i18n from '@/i18n';
import router from '@/router';
import { useUserStore } from '@/stores/useUserStore';
import { AxiosError } from 'axios';
import { ref } from 'vue';
import { usePlatform } from './usePlatform';
import { useEnvStore } from '@/stores/envStore';

const isFetching = ref(false);
export const previousCreditsLeft = ref<number | null>(null);
const AUTH_REDIRECT_COOLDOWN_MS = 2000;
let authRedirectPromise: Promise<void> | null = null;
let lastAuthRedirectAt = 0;
// Function to check internet connectivity
async function checkInternetConnectivity(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }

  // Avoid blocking startup requests on an external connectivity probe.
  // The real API request remains the source of truth for reachability.
  return true;
}

const t = (key) => i18n.global.t(key);
export function useFetch() {
  const { makeRequest, isIOS, isNative } = usePlatform();

  async function handleUnauthorizedRedirect() {
    const now = Date.now();

    if (authRedirectPromise && now - lastAuthRedirectAt < AUTH_REDIRECT_COOLDOWN_MS) {
      await authRedirectPromise;
      return;
    }

    lastAuthRedirectAt = now;

    const userStore = useUserStore();

    authRedirectPromise = (async () => {
      userStore.clearUserData();

      // Only redirect to login if the current route requires authentication.
      // Avoids overriding a deep-link navigation to a public route (e.g. reset-password).
      const currentRoute = router.currentRoute.value;
      const requiresAuth = currentRoute.meta.securityConfig?.requireAuthentication;
      if (requiresAuth && currentRoute.name !== 'login') {
        await router.replace({ name: 'login' });
      }
    })();

    try {
      await authRedirectPromise;
    } finally {
      setTimeout(() => {
        authRedirectPromise = null;
      }, AUTH_REDIRECT_COOLDOWN_MS);
    }
  }

  async function fetchData(
    url: string,
    method: 'GET' | 'DELETE' | 'POST' | 'PUT' | 'PATCH' = 'GET',
    body?: object | FormData,
    timeoutParam: number = 60000,
  ) {
    const envStore = useEnvStore();
    if (envStore.environment !== 'local') {
      url = url.replace('http://', 'https://');
    }

    console.log(`[useFetch] Starting ${method} request to: ${url}`);
    console.log(`[useFetch] Body type: ${body?.constructor?.name || 'none'}`);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let error: any = null;
    let statusCode: number = 200;

    isFetching.value = true;

    // If a session refresh or logout is already running, wait for it to finish to avoid duplicate error toasts
    const userStore = useUserStore();

    if (userStore.isSessionRefreshInProgress) {
      await userStore.waitForSessionRefresh();

      if (!userStore.user) {
        await handleUnauthorizedRedirect();
        const error = {
          code: 'ERR_UNAUTHORIZED',
          message: t('errors.unauthorized'),
          path: url,
          timestamp: new Date(),
          status: 401,
          suppressToast: true,
        } as const;

        return { data, error, statusCode: 401 } as const;
      }
    }

    if (authRedirectPromise) {
      await handleUnauthorizedRedirect();
      const error = {
        code: 'ERR_UNAUTHORIZED',
        message: t('errors.unauthorized'),
        path: url,
        timestamp: new Date(),
        status: 401,
        suppressToast: true,
      } as const;

      return { data, error, statusCode: 401 } as const;
    }

    try {
      // Check internet connectivity first
      const hasInternet = await checkInternetConnectivity();
      if (!hasInternet) {
        throw new Error('NO_INTERNET');
      }

      if ((method === 'POST' || method === 'PUT') && !body) {
        throw new Error('MISSING_BODY');
      }

      // For native platforms, ensure data is serializable
      const processedBody = body;
      if (isNative.value && body && !(body instanceof FormData)) {
        try {
          // Test if the body can be serialized
          JSON.parse(JSON.stringify(body));
          console.log('[useFetch] Body is serializable');
        } catch (serializationError) {
          console.error('[useFetch] Body serialization test failed:', serializationError);
          throw new Error('SERIALIZATION_ERROR');
        }
      }

      // Use longer timeout for iOS
      const timeout = isIOS.value ? Math.max(timeoutParam, 30000) : timeoutParam;

      console.log('[useFetch] Making request with timeout:', timeout);

      // Use the unified request method
      const response = await makeRequest({
        url,
        method,
        data: processedBody,
        headers:
          processedBody instanceof FormData
            ? {} // don't set Content-Type for multipart
            : { 'Content-Type': 'application/json' },
        timeout: timeout,
      });

      console.log('[useFetch] Request completed:', {
        status: response.status,
        hasData: !!response.data,
      });

      data = response.data;
      statusCode = response.status;

      // CapacitorHttp doesn't throw on non-2xx status codes (unlike axios),
      // so we need to manually throw to enter the error handling flow
      if (isNative.value && statusCode >= 400) {
        const nativeError = new Error(`Request failed with status ${statusCode}`) as unknown as {
          response: { status: number; data: unknown };
        };
        nativeError.response = { status: statusCode, data: response.data };
        throw nativeError;
      }

      // Update user's restaurant info if applicable
      if (data.restaurant && data.restaurant.total_credits !== undefined) {
        const userStore = useUserStore();
        userStore.updateCredits(data.restaurant.total_credits);
      }
      if (data.active_restaurant && data.active_restaurant.subscription) {
        const userStore = useUserStore();
        userStore.updateSubscription(data.active_restaurant.subscription);
      }
    } catch (err) {
      console.error('[useFetch] Request failed:', err);

      // Handle custom errors first
      if (err instanceof Error) {
        if (err.message === 'NO_INTERNET') {
          statusCode = 0;
          error = {
            code: 'ERR_NO_INTERNET',
            message: t('errors.noInternet'),
            path: url,
            timestamp: new Date(),
            status: 0,
          };
          return { data, error, statusCode };
        }

        if (err.message === 'MISSING_BODY') {
          statusCode = 400;
          error = {
            code: 'ERR_MISSING_BODY',
            message: t('errors.missingBody'),
            path: url,
            timestamp: new Date(),
            status: 400,
          };
          return { data, error, statusCode };
        }

        if (err.message === 'SERIALIZATION_ERROR') {
          statusCode = 400;
          error = {
            code: 'ERR_SERIALIZATION',
            message: 'Data cannot be serialized for native request',
            path: url,
            timestamp: new Date(),
            status: 400,
          };
          return { data, error, statusCode };
        }
      }

      const axiosError = err as AxiosError;

      if (axiosError.response) {
        const status = axiosError.response.status;
        statusCode = status;

        if (status === 401) {
          await handleUnauthorizedRedirect();

          error = {
            code: 'ERR_UNAUTHORIZED',
            message: t('errors.unauthorized'),
            path: url,
            timestamp: new Date(),
            status: 401,
            suppressToast: true,
          };

          return { data, error, statusCode };
        }

        // **NEW: Handle incomplete registration**
        const responseData = axiosError.response.data as {
          error_code?: string;
          registration_completed?: boolean;
          has_restaurant?: boolean;
          redirect?: string;
        };

        // Check if this is a registration incomplete error
        if (status === 403 && responseData.registration_completed === false) {
          console.log('[useFetch] Registration incomplete detected, redirecting...');

          const userStore = useUserStore();
          if (userStore.user) {
            userStore.user.registration_completed = false;
          }

          // Only redirect if not already on the validate-restaurant page
          if (router.currentRoute.value.name !== 'validate-restaurant') {
            router.push({ name: 'validate-restaurant' });
          }

          error = {
            code: 'ERR_REGISTRATION_INCOMPLETE',
            message: t('errors.registrationIncomplete'),
            path: url,
            timestamp: new Date(),
            status: 403,
          };
          return { data, error, statusCode };
        }

        // **NEW: Handle no restaurant error**
        if (status === 403 && responseData.has_restaurant === false) {
          console.log('[useFetch] No restaurant detected, redirecting...');

          const userStore = useUserStore();
          if (userStore.user) {
            // Mark that user has no restaurant
            userStore.user.restaurants = [];
          }

          // Only redirect if not already on the no-restaurant page
          if (router.currentRoute.value.name !== 'no-restaurant') {
            router.push({ name: 'no-restaurant' });
          }

          error = {
            code: 'ERR_NO_RESTAURANT',
            message: t('errors.noRestaurant'),
            path: url,
            timestamp: new Date(),
            status: 403,
          };
          return { data, error, statusCode };
        }

        // Handle authentication errors
        if (status === 401 || status === 403) {
          const userStore = useUserStore();
          userStore.refreshUser();
        }

        if (responseData.error_code === 'INSUFFICIENT_CREDITS') {
          error = {
            code: 'INSUFFICIENT_CREDITS',
            message: t('errors.insufficientCredits'),
            path: url,
            timestamp: new Date(),
            status: 403,
          };
        } else {
          // Handle different HTTP status codes with specific messages
          let messageKey = 'errors.serverError';

          switch (status) {
            case 400:
              messageKey = 'errors.badRequest';
              break;
            case 401:
              messageKey = 'errors.unauthorized';
              break;
            case 403:
              messageKey = 'errors.forbidden';
              break;
            case 404:
              messageKey = 'errors.notFound';
              break;
            case 422:
              messageKey = 'errors.validationError';
              break;
            case 429:
              messageKey = 'errors.tooManyRequests';
              break;
            case 500:
              messageKey = 'errors.internalServerError';
              break;
            case 502:
            case 503:
            case 504:
              messageKey = 'errors.serviceUnavailable';
              break;
            default:
              messageKey = 'errors.serverError';
          }

          error = {
            code: 'ERR_BAD_RESPONSE',
            details: axiosError.response.data,
            message: t(messageKey),
            path: url,
            timestamp: new Date(),
            status: status,
          };
        }
      } else if (axiosError.code === 'ECONNABORTED') {
        // Timeout error
        statusCode = 408;
        error = {
          code: 'ERR_TIMEOUT',
          message: t('errors.timeout'),
          details: axiosError.message,
          path: url,
          timestamp: new Date(),
          status: 408,
        };
      } else {
        // Network error or other error
        statusCode = 0;
        error = {
          code: 'ERR_NETWORK',
          message: t('errors.networkError'),
          details: axiosError.message,
          path: url,
          timestamp: new Date(),
          status: 0,
        };
      }
    } finally {
      isFetching.value = false;
    }

    return { data, error, statusCode };
  }

  return { fetchData, isFetching };
}
